import { useCallback } from 'react';
import { useCloneService } from '@atlassian/jira-clone-issue/src/services/clone/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { useProjectKey } from '@atlassian/jira-issue-context-service/src/main.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useProjectType } from '@atlassian/jira-project-context-service/src/main.tsx';
import {
	type MenuNode,
	MenuNodeType,
} from '@atlassian/jira-software-context-menu/src/common/types.tsx';
import { useBacklogSelector } from '../../../../../../state/index.tsx';
import { getCards } from '../../../../../../state/selectors/cards/index.tsx';
import { canCreateIssues } from '../../../../../../state/selectors/permissions/index.tsx';
import { CLONE_ISSUE_MENU_ID } from '../../constants.tsx';
import messages from '../../messages.tsx';
import type { ContentProps, Props } from './types.tsx';

export const useCloneIssueContent = ({
	isCloneEnabled,
	onCloneIssue,
}: ContentProps): MenuNode[] => {
	const { formatMessage } = useIntl();

	if (!isCloneEnabled) return [];
	return [
		{
			type: MenuNodeType.Item,
			label: formatMessage(messages.clone),
			id: CLONE_ISSUE_MENU_ID,
			onClick: onCloneIssue,
		},
	];
};

export const useCloneIssue = ({ issueKey, issueId, isMultiSelect }: Props) => {
	const projectKey = useProjectKey();
	const projectType = useProjectType(projectKey);
	const [, makeCloneRequest] = useCloneService(issueKey, projectType || null);
	const issue = useBacklogSelector((state) => getCards(state)[String(issueId)]);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onCloneIssue = useCallback(() => {
		fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', 'cloneIssue');
		makeCloneRequest({
			summary: `CLONE - ${issue.summary}`,
		});
	}, [issue.summary, makeCloneRequest, createAnalyticsEvent]);

	const canClone = useBacklogSelector(canCreateIssues);

	return useCloneIssueContent({
		isCloneEnabled: canClone && !isMultiSelect,
		onCloneIssue,
	});
};
